import AppObj from 'model/appConfigObj';
import React, { useContext, useEffect, useState } from 'react';
import fileBrowserSettingsApi, { DsEntry } from './api';
import localStorageService from 'service/localStorageService';
import { desktopAppTaskContex } from 'screen/desktop';
import { effectiveHomePath } from 'model/userSession';
import './fileBrowserSettings.css';

export const fileBrowserSettingsConfig = {
  appID: 'fileBrowserSettings',
  showName: 'File Browser Settings',
  onList: false,
  icon: require('./images/settings.png'),
  maxHead: 1,
  req: {
    minSize: { width: 520, height: 420 },
    defSize: { width: 640, height: 520 },
  },
};

function parentPath(path: string): string | null {
  const cleaned = path.replace(/\/+$/, '') || '/';
  if (cleaned === '/' || cleaned === '') {
    return null;
  }
  const idx = cleaned.lastIndexOf('/');
  if (idx <= 0) {
    return '/';
  }
  return cleaned.slice(0, idx);
}

function FileBrowserSettings(props: { taskID?: string }) {
  const [desktopControll] = useContext(desktopAppTaskContex);
  const initial = effectiveHomePath(desktopControll?.sessionUser);
  const [path, setPath] = useState(initial);
  const [pathDraft, setPathDraft] = useState(initial);
  const [entries, setEntries] = useState<DsEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const upPath = parentPath(path);

  const load = async (target: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fileBrowserSettingsApi.dsLs(target);
      if (!res.data.success) {
        setError(res.data.message || 'Failed to list path');
        setEntries([]);
      } else {
        setEntries((res.data.data || []).filter((e) => e.directory));
        setPath(target);
        setPathDraft(target);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to list path');
      setEntries([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applySession = (user: any) => {
    localStorageService.saveSessionUser(user);
    desktopControll?.setSessionUser?.(user);
  };

  const closeSelf = () => {
    if (props.taskID) {
      desktopControll?.killTask?.(props.taskID);
    }
  };

  const confirmHome = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fileBrowserSettingsApi.setupHome(path);
      if (!res.data.success) {
        setError(res.data.message || 'Cannot set home');
        return;
      }
      applySession(res.data.data);
      closeSelf();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Cannot set home');
    } finally {
      setBusy(false);
    }
  };

  const applySmbRoot = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fileBrowserSettingsApi.setupHome('/');
      if (!res.data.success) {
        setError(res.data.message || 'Cannot use SMB root');
        return;
      }
      applySession(res.data.data);
      closeSelf();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Cannot use SMB root');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fileBrowserSettings">
      <h2>File Browser Settings</h2>
      <p>
        Choose the folder File Browser uses as Home. <code>/</code> is SMB root.
        If Home is not set yet, this window opens when File Browser starts.
      </p>
      <div className="fileBrowserSettingsPath">
        <input
          value={pathDraft}
          onChange={(e) => setPathDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void load(pathDraft.trim() || '/');
          }}
        />
        <button disabled={busy} onClick={() => void load(pathDraft.trim() || '/')}>Go</button>
      </div>
      {error && <div className="fileBrowserSettingsError">{error}</div>}
      <div className="fileBrowserSettingsList">
        {upPath && (
          <button
            type="button"
            className="fileBrowserSettingsEntry up"
            disabled={busy}
            onClick={() => void load(upPath)}
            onDoubleClick={() => void load(upPath)}
          >
            ..
          </button>
        )}
        {entries.map((e) => (
          <button
            key={e.path}
            type="button"
            className="fileBrowserSettingsEntry"
            disabled={busy}
            onClick={() => void load(e.path)}
            onDoubleClick={() => void load(e.path)}
          >
            {e.name}
          </button>
        ))}
        {!busy && entries.length === 0 && (
          <div className="fileBrowserSettingsHint">
            SMB 已連線，但此伺服器在 <code>/</code> 不會自動顯示 share 名稱。
            請到 TrueNAS → <strong>Shares</strong> 看 SMB share 的<strong>實際名稱</strong>，
            輸入 <code>/share名/你的資料夾</code> 後按 Go。
          </div>
        )}
      </div>
      <div className="fileBrowserSettingsActions">
        <button className="primary" disabled={busy} onClick={() => void confirmHome()}>
          Use this path as home
        </button>
        <button disabled={busy} onClick={() => void applySmbRoot()}>
          Use SMB root
        </button>
      </div>
      <div className="fileBrowserSettingsSelected">Selected: <code>{path}</code></div>
    </div>
  );
}

const App: AppObj = {
  config: fileBrowserSettingsConfig,
  body: FileBrowserSettings,
};

export default App;
